import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import type { Quotation } from '~/api/quotations/types';

import { HttpNotFoundError } from '~/api/errors';
import { useCreateInvoiceFromQuotation } from '~/api/invoices';
import { useDownloadQuotationCoFiles, useDuplicateQuotation, useViewQuotation } from '~/api/quotations';
import { AlertDialog, Button, Menu } from '~/components';
import { optionsMenuTriggerStyle } from '~/components/Table/TableRow.css';
import { r, routes } from '~/providers/RouterProvider/router.routes';
import { isApprovedOrRejected } from '~/utils/quotations';
import { toast } from '~/utils/toast';

import type { DialogHandles } from '../QuotationsTable.hooks';

import { QuotationPreviewDialog } from '../components/QuotationPreviewDialog';

type Props = {
  dialogHandles: DialogHandles;
  quotation: Quotation;
};

export const QuotationOptionsCell = ({ dialogHandles, quotation }: Props) => {
  const createInvoiceFromQuotationMutation = useCreateInvoiceFromQuotation(quotation.id);
  const viewMutation = useViewQuotation(quotation.id);
  const duplicateMutation = useDuplicateQuotation(quotation.id);
  const downloadCoFilesMutation = useDownloadQuotationCoFiles();

  const navigate = useNavigate();
  const { t } = useTranslation(['common', 'quotations']);

  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Menu.Root>
        <Menu.Trigger render={<Button aria-label={t('common:actions.openMenu')} className={optionsMenuTriggerStyle} icon="MoreVert" />} />

        <Menu.List>
          {!isApprovedOrRejected(quotation) && (
            <Menu.Item
              icon="Edit"
              onClick={() =>
                navigate(r(quotation.status === 'Draft' ? routes.createQuotation : routes.editQuotation, { quotationId: quotation.id }))
              }
            >
              {t('common:actions.edit')}
            </Menu.Item>
          )}

          {quotation.status !== 'Draft' && !isApprovedOrRejected(quotation) && (
            <AlertDialog.Trigger
              handle={dialogHandles.approveQuotationHandle}
              nativeButton={false}
              payload={quotation}
              render={<Menu.Item icon="Check">{t('quotations:overview.rowActions.allow')}</Menu.Item>}
            />
          )}

          {quotation.status !== 'Draft' && !isApprovedOrRejected(quotation) && (
            <AlertDialog.Trigger
              handle={dialogHandles.rejectQuotationHandle}
              nativeButton={false}
              payload={quotation}
              render={<Menu.Item icon="CloseSmall">{t('quotations:overview.rowActions.decline')}</Menu.Item>}
            />
          )}

          <Menu.Item
            disabled={viewMutation.isPending}
            icon="Visibility"
            onClick={() => viewMutation.mutate(undefined, { onError: () => toast.danger({ description: t('common:error') }) })}
          >
            {t('quotations:overview.rowActions.preview')}
          </Menu.Item>

          {quotation.status === 'Approved' && (
            <Menu.Item
              disabled={createInvoiceFromQuotationMutation.isPending}
              icon="RequestQuote"
              onClick={() =>
                createInvoiceFromQuotationMutation.mutate(undefined, {
                  onSuccess: ({ id }) => navigate(r(routes.createInvoice, { invoiceId: id })),
                  onError: () => toast.danger({ description: t('common:error') }),
                })
              }
            >
              {t('quotations:overview.rowActions.invoice')}
            </Menu.Item>
          )}

          {quotation.status !== 'Draft' && (
            <Menu.Item
              disabled={duplicateMutation.isPending}
              icon="EditDocument"
              onClick={() =>
                duplicateMutation.mutate(undefined, {
                  onSuccess: ({ id }) => navigate(r(routes.createQuotation, { quotationId: id })),
                  onError: () => toast.danger({ description: t('common:error') }),
                })
              }
            >
              {t('quotations:overview.rowActions.duplicate')}
            </Menu.Item>
          )}

          {quotation.status !== 'Draft' && (
            <Menu.Item
              disabled={downloadCoFilesMutation.isPending}
              icon="Download"
              onClick={() =>
                downloadCoFilesMutation.mutate(
                  { ids: [quotation.id] },
                  {
                    onError: (error) =>
                      toast.danger({
                        description:
                          error instanceof HttpNotFoundError ? t('quotations:alerts.missingFcFiles', { count: 1 }) : t('common:error'),
                      }),
                  },
                )
              }
            >
              {t('quotations:overview.rowActions.download')}
            </Menu.Item>
          )}

          {quotation.status === 'Draft' && (
            <AlertDialog.Trigger
              handle={dialogHandles.deleteQuotationHandle}
              nativeButton={false}
              payload={quotation}
              render={<Menu.Item icon="Trash">{t('common:actions.delete')}</Menu.Item>}
            />
          )}
        </Menu.List>
      </Menu.Root>

      <QuotationPreviewDialog
        html={viewMutation.data}
        onClose={() => viewMutation.reset()}
        title={quotation.title || quotation.description}
      />
    </div>
  );
};
